import { DateRangeOption } from '../../date-range-option';
import { buildDateRanges } from '../../../helpers/build-date-ranges';

import type { TDateRangeFilterConfig } from '../../../types/filter-config';
import { useTableViewHelpers } from '@design-system/molecules/ticlick-table/hooks/use-table-view-helpers';
import { buildDateRangeFacetKey } from '../../../helpers/build-date-range-facet-key';
import { splitDateRangeFacetKey } from '../../../helpers/split-date-range-facet-key';
import { FilterOptionItem } from '@src/design-system/filter-new';
import {
  FILTER_NONE_LABEL,
  FILTER_NONE_VALUE,
  isNoneFilterValue,
} from '../../../helpers/filter-none';
import { useShowsNoneOption } from '../../../helpers/use-shows-none-option';

export type DateOption = {
  value: string;
  label: string;
};

export function DateRangeFilterPanel({
  config,
}: {
  config: TDateRangeFilterConfig;
}) {
  const { entityType, facetName, includedDateRanges } = config;
  const { setFacetValue, getSelectedStaticFilterFacet } = useTableViewHelpers();
  const showNoneOption = useShowsNoneOption(config.withNoneOption);

  const [afterKey, beforeKey] = splitDateRangeFacetKey(facetName);

  const ranges = buildDateRanges({
    afterKey,
    beforeKey,
    includeOnly: includedDateRanges,
  });

  const facet = getSelectedStaticFilterFacet(config.facetName);

  const updateFilters = (
    valueToSend: Record<string, string>,
    optionLabel: string
  ) => {
    const value = buildDateRangeFacetKey({
      before: valueToSend[beforeKey],
      after: valueToSend[afterKey],
    });

    setFacetValue({
      field: facetName,
      value: [value],
      friendlyValue: [optionLabel],
      entityType,
    });
  };

  return (
    <div className='overflow-auto'>
      {ranges.map((option) => (
        <DateRangeOption
          key={option.label}
          isSelected={facet?.friendlyValue?.[0] === option.label}
          label={option.label}
          type={option.type}
          isSingleDate={!!option.isSingleDate}
          onSingleDateValueChange={(value) => {
            const selectedDate = value?.toString();
            const date = option.createDate(selectedDate);
            updateFilters(
              date,
              option.friendlyLabel({
                date: selectedDate,
              })
            );
          }}
          onRangeDateValueChange={(value) => {
            const selectedStartDate = value?.start?.toString();
            const selectedEndDate = value?.end?.toString();
            const date = option.createDate(selectedStartDate, selectedEndDate);

            updateFilters(
              date,
              option.friendlyLabel({
                before: selectedStartDate,
                after: selectedEndDate,
              })
            );
          }}
          onOptionClick={() => {
            const date = option.createDate();
            updateFilters(date, option.friendlyLabel({}));
          }}
        />
      ))}
      {showNoneOption ? (
        <FilterOptionItem
          onClick={() => {
            const isSelected = isNoneFilterValue(facet?.value);
            setFacetValue({
              field: facetName,
              entityType,
              value: isSelected ? [] : [FILTER_NONE_VALUE],
              friendlyValue: isSelected ? [] : [FILTER_NONE_LABEL],
            });
          }}
          selected={isNoneFilterValue(facet?.value)}
          className='border-t border-pale-border'
        >
          {FILTER_NONE_LABEL}
        </FilterOptionItem>
      ) : null}
    </div>
  );
}
